import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { APP } from 'app/AppRoutes';
import { useDispatch, useSelector } from 'react-redux';
import {
  Loading,
  Modal,
  SkeletonText,
  TooltipDefinition,
  Button,
  Checkbox
} from 'carbon-components-react';
import {
  CheckmarkFilled16,
  CloudDownload16,
  DocumentPdf16
} from '@carbon/icons-react';
import {
  selectDownloadingCombinedZip,
  selectDownloadingCombinedPdf,
  selectZipDownloadStatus,
  selectPDFDownloadStatus,
  resetDownloadEnquiryCombinedFile,
  downloadEnquiryCombinedFileStart,
  getEnquiryConfirmationPDFDownloadUrlStart,
  selectLoadingEnquiryConfirmationPDFDownloadUrl,
  selectEnquiryConfirmationPDFDownloadUrl,
  resetEnquiryConfirmationPDFDownloadUrl,
  getDownloadAdvancedOptionsStart,
  resetDownloadAdvancedOptions,
  selectDownloadAdvancedOptions,
  selectIsLoadingDownloadAdvancedOptions
} from 'redux/enquiries';
import { selectIsFromSwxLocate } from 'redux/session/selectors';
import {
  DownloadAdvancedOptions,
  DownloadAdvancedOptionsFile,
  LocalDownloadAdvancedOptions,
  LocalDownloadAdvancedOptionsData,
  LocalDownloadAdvancedOptionsDataFile
} from 'types';
import AdvancedOptions from './AdvancedOptions';
import { PDF_DOWNLOAD_SUPPORTED_EXTENSIONS } from 'appConstants/enquiry';
import { PDFReader } from 'components';
import { Download20, Close20 } from '@carbon/icons-react';
import { useDeviceType } from 'customHooks/useDeviceType';
import { isMobileOnly, isIOS } from 'react-device-detect';

const LoadingBar = (): React.ReactElement => (
  <span className="get-plans-actions-downloads-text-loading">
    <Loading small withOverlay={false} />
  </span>
);

const defaultAdvancedOptions = (
  item: DownloadAdvancedOptions[],
  type: 'zip' | 'pdf' | string
): LocalDownloadAdvancedOptions => {
  let data: LocalDownloadAdvancedOptionsData[] = [];
  let totalAttachmentCount = 0;
  data = item.map((item: DownloadAdvancedOptions) => {
    let supportedCount = 0;
    let commonProps = {
      isSelected: false,
      isExpanded: false,
      indeterminate: false
    };

    if (item.isCompleted) {
      commonProps = {
        isSelected: true,
        isExpanded: true,
        indeterminate: true
      };
    }

    return {
      ...item,
      ...commonProps,
      indeterminate: false,
      attachments: item.attachments.map(
        (attachment: DownloadAdvancedOptionsFile) => {
          const localAttachment: LocalDownloadAdvancedOptionsDataFile = {
            ...attachment,
            isSupported: false,
            isSelected: false,
            indeterminate: false,
            isExpanded: false
          };

          if (['attachment', 'response', 'mail'].includes(attachment.type)) {
            if (['response', 'mail'].includes(attachment.type)) {
              localAttachment.isSupported = true;
              localAttachment.isSelected = true;
            } else {
              const isSupported =
                type === 'pdf'
                  ? PDF_DOWNLOAD_SUPPORTED_EXTENSIONS.includes(
                      attachment.extension || ''
                    )
                  : true;
              localAttachment.isSupported = isSupported;
              localAttachment.isSelected = isSupported;
            }

            if (localAttachment.isSelected) {
              supportedCount++;
              totalAttachmentCount++;
            }
          }

          return localAttachment;
        }
      ),
      supportAttachmentCount: supportedCount
    };
  });

  return { data, totalAttachmentCount };
};

export const Downloads: React.FC<Props | any> = (props: Props) => {
  const {
    allResponsesReceived,
    enquiryId,
    shareId,
    responsesExpired,
    isLoading,
    confirmationFileId,
    setIsDownloadPdf,
    setIsDownloadZip,
    lualMapZoomLevel
  } = props;

  const dispatch = useDispatch();
  const downloadingCombinedZip = useSelector(selectDownloadingCombinedZip);
  const downloadingCombinedPdf = useSelector(selectDownloadingCombinedPdf);
  const zipDownloadStatus = useSelector(selectZipDownloadStatus);
  const pdfDownloadStatus = useSelector(selectPDFDownloadStatus);
  const isFromSwxLocate = useSelector(selectIsFromSwxLocate);
  const downloadAdvancedOptions = useSelector(selectDownloadAdvancedOptions);
  const isLoadingDownloadAdvancedOptions = useSelector(
    selectIsLoadingDownloadAdvancedOptions
  );
  const isLoadingConfirmationPDFDownloadUrl = useSelector(
    selectLoadingEnquiryConfirmationPDFDownloadUrl
  );
  const confirmationPDFDownloadUrl = useSelector(
    selectEnquiryConfirmationPDFDownloadUrl
  );
  const [isDownloadZipSuccessful, setIsDownloadZipSuccessful] = useState<any>(
    undefined
  );
  const [isDownloadPdfSuccessful, setIsDownloadPdfSuccessful] = useState<any>(
    undefined
  );

  // Note: >= 16 means zoom level 16 and above are considered good for LUAL maps
  const effectiveZoomLevel =
    lualMapZoomLevel !== null &&
    lualMapZoomLevel !== undefined &&
    lualMapZoomLevel >= 16
      ? true
      : false;

  const [
    isDownloadBYDAConfirmationPDFSuccesful,
    setIsDownloadBYDAConfirmationPDFSuccesful
  ] = useState<boolean | undefined>(undefined);
  const [disableZipButton, setDisableZipButton] = useState<boolean>(false);
  const [disablePDFButton, setDisablePDFButton] = useState<boolean>(false);
  const [showPDFModal, setShowPDFModal] = useState<boolean>(false);
  const [
    disableBYDAConfirmationButton,
    setDisableBYDAConfirmationButton
  ] = useState<boolean>(false);
  const [showZipModal, setShowZipModal] = useState<boolean>(false);
  const [includeLookUpAndLive, setIncludeLookUpAndLive] = useState<boolean>(
    false
  );
  const [
    includeLookUpAndLivePdf,
    setIncludeLookUpAndLivePdf
  ] = useState<boolean>(false);
  const [showCompilingZipModal, setShowCompilingZipModal] = useState<boolean>(
    false
  );
  const [showCompilingPdfModal, setShowCompilingPdfModal] = useState<boolean>(
    false
  );
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
  const [
    localDownloadAdvancedOptions,
    setLocalDownloadAdvancedOptions
  ] = useState<LocalDownloadAdvancedOptions>({
    data: [],
    totalAttachmentCount: 0
  });
  const [
    showFailDownloadPdfModal,
    setShowFailDownloadPdfModal
  ] = useState<boolean>(false);
  const [
    showFailDownloadZipModal,
    setShowFailDownloadZipModal
  ] = useState<boolean>(false);
  const [
    showBYDAConfirmationModal,
    setShowBYDAConfirmationModal
  ] = useState<boolean>(false);
  const [bydaConfirmationPDFUrl, setBydaConfirmationPDFUrl] = useState<
    string | null
  >(null);
  const [isModalMode, setIsModalMode] = useState<boolean>(false);
  const { isMobileOrTablet } = useDeviceType();
  const fullModal =
    isAccordionOpen &&
    !isLoadingDownloadAdvancedOptions &&
    localDownloadAdvancedOptions.data.length > 0;

  useEffect(() => {
    // for PDF
    if (typeof downloadingCombinedPdf === 'number') {
      setDisablePDFButton(true);
      if (pdfDownloadStatus === true) {
        setTimeout(() => {
          dispatch(resetDownloadEnquiryCombinedFile({ fileType: 'pdf' }));
          setDisablePDFButton(false);
          setIncludeLookUpAndLivePdf(false);
        }, 1000);
      }
    } else {
      setDisablePDFButton(false);
    }

    // for ZIP
    if (typeof downloadingCombinedZip === 'number') {
      setDisableZipButton(true);

      if (zipDownloadStatus === true) {
        setTimeout(() => {
          dispatch(resetDownloadEnquiryCombinedFile({ fileType: 'zip' }));
          setDisableZipButton(false);
          setIncludeLookUpAndLive(false);
        }, 1000);
      }
    } else {
      setDisableZipButton(false);
    }
  }, [
    downloadingCombinedZip,
    downloadingCombinedPdf,
    isDownloadZipSuccessful,
    isDownloadPdfSuccessful,
    zipDownloadStatus,
    pdfDownloadStatus,
    dispatch
  ]);

  useEffect(() => {
    if (
      isDownloadZipSuccessful !== undefined &&
      isDownloadZipSuccessful === false &&
      downloadingCombinedZip === undefined
    ) {
      setIsDownloadZipSuccessful(true);
      setTimeout(() => {
        setIsDownloadZipSuccessful(undefined);
        setIncludeLookUpAndLive(false);
      }, 2000);
    }

    if (
      isDownloadPdfSuccessful !== undefined &&
      isDownloadPdfSuccessful === false &&
      downloadingCombinedPdf === undefined
    ) {
      setIsDownloadPdfSuccessful(true);
      setTimeout(() => {
        setIsDownloadPdfSuccessful(undefined);
        setIncludeLookUpAndLivePdf(false);
      }, 2000);
    }
    // eslint-disable-next-line
  }, [downloadingCombinedZip, downloadingCombinedPdf]);

  useEffect(() => {
    if (isLoadingConfirmationPDFDownloadUrl) {
      setDisableBYDAConfirmationButton(true);
    }

    if (!isLoadingConfirmationPDFDownloadUrl && confirmationPDFDownloadUrl) {
      // Check if we have a waiting window from iOS on-demand loading
      // @ts-ignore - Adding custom property to window object
      const waitingWindow = window.__confirmationPdfWindow;

      if (waitingWindow && isIOS && isMobileOrTablet) {
        // iOS on-demand path - navigate the already-opened window
        try {
          waitingWindow.location.replace(confirmationPDFDownloadUrl);
          // @ts-ignore - Deleting custom property from window object
          delete window.__confirmationPdfWindow;
        } catch (error) {
          console.error('Failed to navigate window:', error);
          try {
            waitingWindow.close();
          } catch (e) {
            console.error('Failed to close window:', e);
          }
          // Fallback to modal
          setBydaConfirmationPDFUrl(confirmationPDFDownloadUrl);
          setShowBYDAConfirmationModal(true);
        }
        setDisableBYDAConfirmationButton(false);
      } else if (isModalMode) {
        // Show in modal
        setBydaConfirmationPDFUrl(confirmationPDFDownloadUrl);
        setShowBYDAConfirmationModal(true);
        setDisableBYDAConfirmationButton(false);
        setIsModalMode(false); // Reset the flag
      } else {
        // Original behavior - direct download
        const anchor = document.createElement('a');
        anchor.href = confirmationPDFDownloadUrl;
        anchor.click();
        anchor?.parentNode?.removeChild(anchor);
        setDisableBYDAConfirmationButton(false);
        setIsDownloadBYDAConfirmationPDFSuccesful(true);
        setTimeout(
          () => setIsDownloadBYDAConfirmationPDFSuccesful(undefined),
          2000
        );
      }
      dispatch(resetEnquiryConfirmationPDFDownloadUrl());
    }
  }, [
    isLoadingConfirmationPDFDownloadUrl,
    confirmationPDFDownloadUrl,
    isModalMode,
    dispatch,
    isMobileOrTablet
  ]);

  useEffect(() => {
    if (typeof downloadingCombinedZip === 'number') {
      setShowCompilingZipModal(true);
    }
    if (
      (downloadingCombinedZip === undefined && zipDownloadStatus === true) ||
      typeof downloadingCombinedZip !== 'number'
    ) {
      setShowCompilingZipModal(false);
      setShowZipModal(false);
    }

    // Also close modal when download status changes to undefined (completion)
    if (
      downloadingCombinedZip === undefined &&
      zipDownloadStatus === undefined
    ) {
      setShowCompilingZipModal(false);
      setShowZipModal(false);
    }
    // Close modal when there's an error (string indicates error)
    if (typeof downloadingCombinedZip === 'string') {
      setShowCompilingZipModal(false);
      setShowZipModal(false);

      // Check if this is a LUAL generation failure
      if (
        downloadingCombinedZip === 'LUAL_GENERATION_FAILED' ||
        downloadingCombinedZip.includes('LUAL_GENERATION_FAILED') ||
        downloadingCombinedZip.includes('LUAL') ||
        downloadingCombinedZip.includes('ProtocolError') ||
        downloadingCombinedZip.includes('timeout')
      ) {
        setShowFailDownloadZipModal(true);
      }
    }

    // Handle error object format from saga
    if (
      downloadingCombinedZip &&
      typeof downloadingCombinedZip === 'object' &&
      'error' in downloadingCombinedZip
    ) {
      setShowCompilingZipModal(false);
      setShowZipModal(false);

      // Check if this is a LUAL generation failure
      let errorMessage: string | undefined;
      if (
        downloadingCombinedZip &&
        typeof downloadingCombinedZip === 'object' &&
        'error' in downloadingCombinedZip
      ) {
        const zipObj = downloadingCombinedZip;
        const errorProp = zipObj['error'];
        if (errorProp && typeof errorProp === 'string') {
          errorMessage = errorProp;
        }
      }
      if (
        errorMessage === 'LUAL_GENERATION_FAILED' ||
        errorMessage?.includes('LUAL_GENERATION_FAILED') ||
        errorMessage?.includes('LUAL') ||
        errorMessage?.includes('ProtocolError') ||
        errorMessage?.includes('timeout')
      ) {
        setShowFailDownloadZipModal(true);
      }
    }

    // Close modal when there's an error (string indicates error)
    if (typeof downloadingCombinedZip === 'string') {
      setShowCompilingZipModal(false);
      setShowZipModal(false);
      setShowFailDownloadZipModal(true);
    }
  }, [downloadingCombinedZip, zipDownloadStatus]);

  useEffect(() => {
    if (typeof downloadingCombinedPdf === 'number') {
      setShowCompilingPdfModal(true);
    }
    if (
      (downloadingCombinedPdf === undefined && pdfDownloadStatus === true) ||
      typeof downloadingCombinedPdf !== 'number'
    ) {
      setShowCompilingPdfModal(false);
      setShowPDFModal(false);
    }
    // Also close modal when download status changes to undefined (completion)
    if (
      downloadingCombinedPdf === undefined &&
      pdfDownloadStatus === undefined
    ) {
      setShowCompilingPdfModal(false);
      setShowPDFModal(false);
    }
    // Close modal when there's an error (string indicates error)
    if (typeof downloadingCombinedPdf === 'string') {
      setShowCompilingPdfModal(false);
      setShowPDFModal(false);

      // Check if this is a LUAL generation failure
      if (
        downloadingCombinedPdf === 'LUAL_GENERATION_FAILED' ||
        downloadingCombinedPdf.includes('LUAL_GENERATION_FAILED') ||
        downloadingCombinedPdf.includes('LUAL') ||
        downloadingCombinedPdf.includes('ProtocolError') ||
        downloadingCombinedPdf.includes('timeout')
      ) {
        setShowFailDownloadPdfModal(true);
      }
    }

    // Handle error object format from saga
    if (
      downloadingCombinedPdf &&
      typeof downloadingCombinedPdf === 'object' &&
      'error' in downloadingCombinedPdf
    ) {
      setShowCompilingPdfModal(false);
      setShowPDFModal(false);

      // Check if this is a LUAL generation failure
      let errorMessage: string | undefined;
      if (
        downloadingCombinedPdf &&
        typeof downloadingCombinedPdf === 'object' &&
        'error' in downloadingCombinedPdf
      ) {
        const pdfObj = downloadingCombinedPdf;
        const errorProp = pdfObj['error'];
        if (errorProp && typeof errorProp === 'string') {
          errorMessage = errorProp;
        }
      }

      if (
        errorMessage &&
        (errorMessage === 'LUAL_GENERATION_FAILED' ||
          errorMessage.includes('LUAL_GENERATION_FAILED') ||
          errorMessage.includes('LUAL') ||
          errorMessage.includes('ProtocolError') ||
          errorMessage.includes('timeout'))
      ) {
        setShowFailDownloadPdfModal(true);
      }
    }
    // Close modal when there's an error (string indicates error)
    if (typeof downloadingCombinedPdf === 'string') {
      setShowCompilingPdfModal(false);
      setShowPDFModal(false);
      setShowFailDownloadPdfModal(true);
    }
  }, [downloadingCombinedPdf, pdfDownloadStatus]);

  useEffect(() => {
    if (showPDFModal || showZipModal) {
      dispatch(getDownloadAdvancedOptionsStart({ enquiryId, shareId }));
    } else {
      dispatch(resetDownloadAdvancedOptions());
    }
  }, [dispatch, enquiryId, showPDFModal, showZipModal, shareId]);

  useEffect(() => {
    let fileType = '';

    showPDFModal && (fileType = 'pdf');
    showZipModal && (fileType = 'zip');

    if (downloadAdvancedOptions && downloadAdvancedOptions.length > 0) {
      const { data, totalAttachmentCount } = defaultAdvancedOptions(
        downloadAdvancedOptions,
        fileType
      );
      setLocalDownloadAdvancedOptions({ data, totalAttachmentCount });
    }
  }, [downloadAdvancedOptions, showPDFModal, showZipModal]);

  const download = (fileType: any, includeLookUpAndLiveParam?: boolean) => {
    let advancedOptions: any;

    if (fileType === 'zip') {
      setIsDownloadZipSuccessful(false);
    }
    if (fileType === 'pdf') {
      setIsDownloadPdfSuccessful(false);
    }

    const haveAdvancedOptions = localDownloadAdvancedOptions.data.some(
      (item: LocalDownloadAdvancedOptionsData) =>
        item.indeterminate || (item.isCompleted && !item.isSelected)
    );
    let selectedAttachmentCount = 0;

    if (haveAdvancedOptions) {
      advancedOptions = {
        hasAdvancedOptions: true,
        totalAttachmentCount: localDownloadAdvancedOptions.totalAttachmentCount,
        reponses: localDownloadAdvancedOptions.data
          .filter((item: LocalDownloadAdvancedOptionsData) => item.isCompleted)
          .reduce((acc: any, item: LocalDownloadAdvancedOptionsData) => {
            if (item.isSelected) {
              item.attachments.forEach(
                (attachment: LocalDownloadAdvancedOptionsDataFile) => {
                  const { type, isSelected, id, responseId } = attachment;

                  if (!isSelected || type === 'divider') return acc;

                  selectedAttachmentCount++;

                  if (!acc[responseId]) {
                    acc[responseId] = {
                      mailBody: false,
                      files: {}
                    };
                  }

                  if (['response', 'mail'].includes(type)) {
                    acc[responseId].mailBody = true;
                  }

                  if (type === 'attachment') {
                    acc[responseId].files[id] = {
                      ...(attachment.pageNumbers
                        ? { pageNumbers: attachment.pageNumbers }
                        : {})
                    };
                  }
                  return acc;
                }
              );
            }
            return acc;
          }, {})
      };

      advancedOptions.selectedAttachmentCount = selectedAttachmentCount;
    }

    dispatch(
      downloadEnquiryCombinedFileStart({
        enquiryId,
        fileType,
        shareId,
        isFromSwxLocate,
        includeLookUpAndLive: includeLookUpAndLiveParam || false,
        lualMapZoomLevel: lualMapZoomLevel ?? undefined,
        advancedOptions
      })
    );
  };

  const handleBYDAConfirmationPDFClick = (): void => {
    const params = {
      fileId: confirmationFileId,
      enquiryId
    };
    if (isMobileOrTablet || isFromSwxLocate) {
      setIsModalMode(false);
      dispatch(getEnquiryConfirmationPDFDownloadUrlStart(params));
    }
  };

  const handleBYDAConfirmationPDFModalClick = (): void => {
    if (isIOS && isMobileOrTablet) {
      // iOS on-demand PDF loading
      setDisableBYDAConfirmationButton(true);

      let newWindow: Window | null = null;

      try {
        // Open window synchronously to bypass popup blocker
        newWindow = window.open('about:blank', '_blank');

        if (!newWindow) {
          // Popup blocked - fallback to modal
          setIsModalMode(true);
          dispatch(
            getEnquiryConfirmationPDFDownloadUrlStart({
              fileId: confirmationFileId,
              enquiryId
            })
          );
          return;
        }

        // Show loading message
        newWindow.document.write(
          '<html><body style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; color: #666;">Loading PDF...</body></html>'
        );

        // Store the window reference for later use in the useEffect
        // @ts-ignore - Adding custom property to window object
        window.__confirmationPdfWindow = newWindow;

        // Trigger the redux action to fetch the URL
        dispatch(
          getEnquiryConfirmationPDFDownloadUrlStart({
            fileId: confirmationFileId,
            enquiryId
          })
        );
      } catch (error) {
        console.error('Failed to open PDF window:', error);
        if (newWindow) {
          try {
            newWindow.close();
          } catch (e) {
            console.error('Failed to close window:', e);
          }
        }
        // Fallback to modal
        setIsModalMode(true);
        dispatch(
          getEnquiryConfirmationPDFDownloadUrlStart({
            fileId: confirmationFileId,
            enquiryId
          })
        );
      }
    } else {
      // Original behavior for non-iOS
      const params = {
        fileId: confirmationFileId,
        enquiryId
      };
      setIsModalMode(true);
      setDisableBYDAConfirmationButton(true);
      dispatch(getEnquiryConfirmationPDFDownloadUrlStart(params));
    }
  };

  const DownloadIcon = (): React.ReactElement => {
    if (typeof downloadingCombinedZip !== 'number') {
      return (
        <>
          {typeof downloadingCombinedZip === 'string' ? (
            <span className="get-plans-actions-downloads-text-failed">
              Failed
            </span>
          ) : isDownloadZipSuccessful ? (
            <CheckmarkFilled16
              className="get-plans-actions-downloads-success-icon-green"
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          ) : (
            <CloudDownload16
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          )}
        </>
      );
    } else {
      return (
        <div className="loading-wrapper">
          <LoadingBar />
        </div>
      );
    }
  };

  // for pdf
  const DownloadIconForPDF = () => {
    if (typeof downloadingCombinedPdf !== 'number') {
      return (
        <>
          {typeof downloadingCombinedPdf === 'string' ? (
            <span className="get-plans-actions-downloads-text-failed">
              Failed
            </span>
          ) : isDownloadPdfSuccessful ? (
            <CheckmarkFilled16
              className="get-plans-actions-downloads-success-icon-green"
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          ) : (
            <DocumentPdf16
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          )}
        </>
      );
    } else {
      return (
        <div className="loading-wrapper">
          <LoadingBar />
        </div>
      );
    }
  };

  const BYDAConfirmationButtonIcon = () => {
    if (isLoadingConfirmationPDFDownloadUrl) {
      return <Loading small withOverlay={false} />;
    } else {
      return (
        <>
          {isDownloadBYDAConfirmationPDFSuccesful ? (
            <CheckmarkFilled16
              className="get-plans-actions-downloads-success-icon-green"
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          ) : (
            <DocumentPdf16
              height={isMobileOrTablet ? 20 : 16}
              width={isMobileOrTablet ? 20 : 16}
            />
          )}
        </>
      );
    }
  };

  const handleShowModal = (open: boolean): void => {
    setIsDownloadPdf && setIsDownloadPdf(true);
    setShowPDFModal(open);
  };

  const handleShowModalZip = (open: boolean): void => {
    setIsDownloadZip && setIsDownloadZip(true);
    setShowZipModal(open);
  };

  const handleBYDADownload = (): void => {
    if (bydaConfirmationPDFUrl) {
      const anchor = document.createElement('a');
      anchor.href = bydaConfirmationPDFUrl;
      anchor.download = 'byda-confirmation.pdf';
      anchor.click();
      anchor?.parentNode?.removeChild(anchor);
    }
  };

  const BYDAConfirmationPDFModal = () => {
    return (
      <Modal
        open={showBYDAConfirmationModal}
        passiveModal
        size="lg"
        className={`super-modal mobile-view mobile-get-plans-super-modal`}
      >
        <div className="mobile-view-modal-header">
          <div className="mobile-view-modal-header-content">
            <div className="mobile-view-modal-header-text">
              <div className="mobile-view-modal-title">BYDA Confirmation</div>
              <div className="mobile-view-modal-subtitle">
                Confirmation Sheet
              </div>
            </div>
            <div className="mobile-view-modal-actions">
              <Button
                kind="ghost"
                size="field"
                onClick={handleBYDADownload}
                className="mobile-view-modal-action-button"
              >
                <Download20 fill="#0F62FE" />
              </Button>
              <Button
                kind="ghost"
                size="field"
                onClick={() => {
                  setShowBYDAConfirmationModal(false);
                  setBydaConfirmationPDFUrl(null);
                }}
                className="mobile-view-modal-action-button"
              >
                <Close20 fill="#0F62FE" />
              </Button>
            </div>
          </div>
        </div>
        <div className="mobile-view-modal-content" style={{ padding: 0 }}>
          {bydaConfirmationPDFUrl && (
            <PDFReader
              fileUrl={bydaConfirmationPDFUrl}
              className="byda-confirmation-pdf-viewer"
            />
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div
      className={`${
        responsesExpired ? '' : 'get-plans-actions-zip-downloads'
      } ${
        isMobileOrTablet ? 'mobile-view-get-plans-actions-zip-downloads' : ''
      }`}
    >
      {ReactDOM.createPortal(
        <Modal
          open={showPDFModal}
          modalHeading="Download as PDF"
          modalLabel=""
          onRequestClose={() => {
            handleShowModal(false);
            setIsAccordionOpen(false);
            setIncludeLookUpAndLivePdf(false);
          }}
          primaryButtonText={'Download'}
          secondaryButtonText={'Cancel'}
          onRequestSubmit={() => {
            download('pdf', includeLookUpAndLivePdf);
            handleShowModal(false);
            setIsAccordionOpen(false);
          }}
          className={`download-as-pdf-modal ${fullModal ? 'fullHeight' : ''} ${
            isMobileOnly ? '' : 'overflow-hidden'
          }`}
          preventCloseOnClickOutside={true}
          size="lg"
        >
          <div className="download-as-pdf-modal-text">
            <div className="download-modal-content">
              Generate a single PDF file combining all of the responses received
              so far.
              <br />
              Some file types may be excluded from the generated file (eg. DWF,
              ZIP), so be sure to check the PDF&apos;s table of contents for any
              excluded files and review them online.
              <Checkbox
                id="includeLookUpAndLivePdf"
                checked={includeLookUpAndLivePdf}
                onChange={(checked) => {
                  setIncludeLookUpAndLivePdf(checked);
                }}
                disabled={!effectiveZoomLevel}
                labelText={
                  <>
                    {effectiveZoomLevel ? (
                      <p className="lual-checkbox-subtitle">
                        Include Look up and Live plan
                        <br />
                        You can also choose to include the Look up and Live
                        plan, but it will take longer to generate the PDF file.
                      </p>
                    ) : (
                      <p className="lual-checkbox-subtitle">
                        The dig site for this enquiry is too large to include a
                        Look up and Live map in the PDF. You can still view the
                        interactive map and details in Look up and Live.
                      </p>
                    )}
                  </>
                }
                className="download-modal-checkbox"
              />
              {showPDFModal && (
                <AdvancedOptions
                  shareId={shareId}
                  enquiryId={isFromSwxLocate ? enquiryId : ''}
                  setIsAccordionOpen={setIsAccordionOpen}
                  type="pdf"
                  isAccordionOpen={isAccordionOpen}
                  localDownloadAdvancedOptions={localDownloadAdvancedOptions}
                  setLocalDownloadAdvancedOptions={
                    setLocalDownloadAdvancedOptions
                  }
                />
              )}
            </div>
          </div>
        </Modal>,
        document.querySelector(`#${APP}`) || document.body
      )}
      {!responsesExpired && (
        <>
          {!isLoading && (
            <>
              <TooltipDefinition
                tooltipText={
                  allResponsesReceived
                    ? 'Download all responses in a single ZIP file'
                    : 'Download all responses in a single ZIP file. Note that we are still waiting for some responses.'
                }
                direction="bottom"
                align="center"
                className="response-expired-tooltip"
              >
                <Button
                  className="main_cont"
                  kind={'secondary'}
                  onClick={() => handleShowModalZip(true)}
                  disabled={disableZipButton}
                  renderIcon={isMobileOrTablet ? null : DownloadIcon}
                >
                  {isMobileOrTablet ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>
                          Download as ZIP
                        </div>
                        <div>
                          <DownloadIcon />
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                        Download all available responses in a single ZIP file.
                      </div>
                    </div>
                  ) : (
                    <div className="get-plans-actions-downloads-text">
                      Download as ZIP
                    </div>
                  )}
                </Button>
              </TooltipDefinition>
              {/* ZIP Download Modal */}
              {ReactDOM.createPortal(
                <Modal
                  open={showZipModal}
                  modalHeading="Download as ZIP"
                  modalLabel=""
                  onRequestClose={() => {
                    handleShowModalZip(false);
                    setIsAccordionOpen(false);
                    setIncludeLookUpAndLive(false);
                  }}
                  primaryButtonText={'Download'}
                  secondaryButtonText={'Cancel'}
                  onRequestSubmit={() => {
                    download('zip', includeLookUpAndLive);
                    handleShowModalZip(false);
                    setIsAccordionOpen(false);
                  }}
                  className={`download-as-zip-modal ${
                    fullModal ? 'fullHeight' : ''
                  } ${isMobileOnly ? '' : 'overflow-hidden'}`}
                  preventCloseOnClickOutside={true}
                  size="sm"
                >
                  <div className="download-as-zip-modal-text">
                    <div className="download-modal-content">
                      Download all available responses in a single ZIP file.
                      <br />
                      <Checkbox
                        id="includeLookUpAndLive"
                        checked={includeLookUpAndLive}
                        onChange={(checked) => {
                          setIncludeLookUpAndLive(checked);
                        }}
                        disabled={!effectiveZoomLevel}
                        labelText={
                          <>
                            {effectiveZoomLevel ? (
                              <p className="lual-checkbox-subtitle">
                                Include Look up and Live plan
                                <br />
                                You can also choose to include the Look up and
                                Live plan, but it will take longer to generate
                                the ZIP file.
                              </p>
                            ) : (
                              <p className="lual-checkbox-subtitle">
                                The dig site for this enquiry is too large to
                                include a Look up and Live map in the ZIP. You
                                can still view the interactive map and details
                                in Look up and Live.
                              </p>
                            )}
                          </>
                        }
                        className="download-modal-checkbox"
                      />
                      {showZipModal && (
                        <AdvancedOptions
                          shareId={shareId}
                          setIsAccordionOpen={setIsAccordionOpen}
                          type="zip"
                          isAccordionOpen={isAccordionOpen}
                          localDownloadAdvancedOptions={
                            localDownloadAdvancedOptions
                          }
                          setLocalDownloadAdvancedOptions={
                            setLocalDownloadAdvancedOptions
                          }
                        />
                      )}
                    </div>
                  </div>
                </Modal>,
                document.querySelector(`#${APP}`) || document.body
              )}
              {ReactDOM.createPortal(
                <Modal
                  open={showFailDownloadPdfModal}
                  modalHeading="Unable to generate Look up and Live map"
                  className="download-without-lual-pdf-modal"
                  passiveModal
                  preventCloseOnClickOutside={true}
                  onRequestClose={() => {
                    setShowFailDownloadPdfModal(false);
                  }}
                  style={{ zIndex: 9999 }}
                  size="sm"
                >
                  <div
                    className="compiling-modal-content"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div>
                      <div
                        className="compiling-modal-description"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        We couldn’t generate the map for this enquiry. Try
                        downloading the PDF/ZIP without including the Look up
                        and Live plan.
                      </div>
                    </div>
                  </div>
                </Modal>,
                document.querySelector(`#${APP}`) || document.body
              )}
              {ReactDOM.createPortal(
                <Modal
                  open={showFailDownloadZipModal}
                  modalHeading="Unable to generate Look up and Live map"
                  className="download-without-lual-pdf-modal"
                  passiveModal
                  preventCloseOnClickOutside={true}
                  onRequestClose={() => {
                    setShowFailDownloadZipModal(false);
                  }}
                  style={{ zIndex: 9999 }}
                  size="sm"
                >
                  <div
                    className="compiling-modal-content"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div>
                      <div
                        className="compiling-modal-description"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        We couldn’t generate the map for this enquiry. Try
                        downloading the PDF/ZIP without including the Look up
                        and Live plan.
                      </div>
                    </div>
                  </div>
                </Modal>,
                document.querySelector(`#${APP}`) || document.body
              )}
              {ReactDOM.createPortal(
                <Modal
                  open={showCompilingZipModal}
                  modalHeading="Compiling ZIP"
                  passiveModal
                  className="compiling-zip-modal"
                  preventCloseOnClickOutside={true}
                  onRequestClose={() => {}} // Disable close functionality
                >
                  <div
                    className="compiling-modal-content"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Loading
                      small
                      withOverlay={false}
                      className="compiling-modal-loading"
                    />
                    <div>
                      <div className="compiling-modal-title">Compiling ZIP</div>
                      <div
                        className="compiling-modal-description"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        Your files are now being prepared. Please keep this
                        window open, closing or refreshing may stop the process.
                        Once completed, your ZIP file will download
                        automatically.
                      </div>
                    </div>
                  </div>
                </Modal>,
                document.querySelector(`#${APP}`) || document.body
              )}
              {ReactDOM.createPortal(
                <Modal
                  open={showCompilingPdfModal}
                  modalHeading="Compiling PDF"
                  passiveModal
                  className="compiling-pdf-modal"
                  preventCloseOnClickOutside={true}
                  onRequestClose={() => {}} // Disable close functionality
                >
                  <div
                    className="compiling-modal-content"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Loading
                      small
                      withOverlay={false}
                      className="compiling-modal-loading"
                    />
                    <div>
                      <div className="compiling-modal-title">Compiling PDF</div>
                      <div
                        className="compiling-modal-description"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        Your files are now being prepared. Please keep this
                        window open, closing or refreshing may stop the process.
                        Once completed, your PDF file will download
                        automatically.
                      </div>
                    </div>
                  </div>
                </Modal>,
                document.querySelector(`#${APP}`) || document.body
              )}
              <TooltipDefinition
                tooltipText={
                  allResponsesReceived
                    ? 'Download all responses in a single PDF document. Some non-printable files may not be included in the output.'
                    : 'Download all responses in a single PDF document. Some non-printable files may not be included in the output. Note that we are still waiting for some responses.'
                }
                direction="bottom"
                align="center"
                className="response-expired-tooltip"
                style={isMobileOrTablet ? { marginBottom: '1rem' } : {}}
              >
                <Button
                  className="main_cont"
                  kind={'secondary'}
                  onClick={() => handleShowModal(true)}
                  disabled={disablePDFButton}
                  renderIcon={isMobileOrTablet ? null : DownloadIconForPDF}
                >
                  {isMobileOrTablet ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>
                          Download as PDF
                        </div>
                        <div>
                          <DownloadIconForPDF />
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                        Download all available responses in a single PDF
                        document. Some non-printable files may not be included
                        in the output.
                      </div>
                    </div>
                  ) : (
                    <div className="get-plans-actions-downloads-text">
                      Download as PDF
                    </div>
                  )}
                </Button>
              </TooltipDefinition>
              {isMobileOrTablet && (
                <>
                  <Button
                    className="main_cont"
                    kind={'secondary'}
                    onClick={handleBYDAConfirmationPDFModalClick}
                    disabled={disableBYDAConfirmationButton}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>
                          View Confirmation Sheet
                        </div>
                        <div>
                          <BYDAConfirmationButtonIcon />
                        </div>
                      </div>
                    </div>
                  </Button>
                </>
              )}

              {confirmationFileId && isFromSwxLocate && !isMobileOrTablet && (
                <Button
                  className="tertiary-black-white"
                  kind={'tertiary'}
                  onClick={handleBYDAConfirmationPDFClick}
                  disabled={disableBYDAConfirmationButton}
                  renderIcon={BYDAConfirmationButtonIcon}
                >
                  BYDA confirmation
                </Button>
              )}
            </>
          )}
          {isLoading && <SkeletonText />}
        </>
      )}
      <BYDAConfirmationPDFModal />
    </div>
  );
};

type ownProps = {
  allResponsesReceived: any;
  enquiryId: number;
  isSmallScreen: boolean;
  shareId?: string;
  responsesExpired: boolean;
  isLoading: boolean;
  confirmationFileId: number;
  setIsDownloadPdf: Function;
  setIsDownloadZip: Function;
  lualExtent?: any;
  lualMapZoomLevel?: number | null;
};

type Props = ownProps;

Downloads.displayName = 'Downloads';

export default React.memo(Downloads);
